import { capitalizeWords } from "Utils/utils";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import InputField from "components/common/Input/Input";
import SelectOption from "components/common/Select/Select";
import { NumericFormat } from "react-number-format";
import { useEffect, useMemo, useRef, useState } from "react";

const MAX_NAME_LENGTH = 150;

const AddDocumentModal = ({
  isOpen,
  onClose,
  onSubmit,
  categories = [],
  preselectedCategoryId = null,
  isLoading = false,
  categoryIdField = "worker_compliance_category_id",
  hideCategoryField = false,
}) => {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [categoryId, setCategoryId] = useState(preselectedCategoryId ?? "");
  const [score, setScore] = useState(0);

  const visibleCategories = useMemo(
    () => categories.filter((c) => c.is_visible !== false),
    [categories]
  );

  const prevOpenRef = useRef(false);
  const submittingRef = useRef(false);
  const categoryMenuOpenRef = useRef(false);
  const skipSubmitRef = useRef(false);

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      const visible = categories.filter((c) => c.is_visible !== false);
      const preselect = preselectedCategoryId != null ? String(preselectedCategoryId) : "";
      const fallback = visible[0]?.id != null ? String(visible[0].id) : "";
      setCategoryId(preselect || fallback);
      setName("");
      setNameError("");
      setScore(0);
      submittingRef.current = false;
      categoryMenuOpenRef.current = false;
      skipSubmitRef.current = false;
    }
    if (!isOpen) {
      submittingRef.current = false;
      if (typeof window !== "undefined") window.__addDocSubmitLock = false;
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, preselectedCategoryId, categories]);


  useEffect(() => {
    if (!isLoading) {
      submittingRef.current = false;
      if (typeof window !== "undefined") window.__addDocSubmitLock = false;
    }
  }, [isLoading]);

  // Use string values so SelectOption matches reliably (newly created category id is always selectable)
  const categoryOptions = useMemo(
    () =>
      visibleCategories.map((c) => ({
        value: c.id != null ? String(c.id) : "",
        label: capitalizeWords(c.name || ""),
      })),
    [visibleCategories]
  );

  const selectedCategoryOption =
    categoryId != null && categoryId !== ""
      ? categoryOptions.find((opt) => opt.value === String(categoryId)) ?? null
      : null;

  // Module-level lock: only one submit in the entire app at a time (survives duplicate modals/events)
  const runSubmit = () => {
    if (typeof window !== "undefined" && window.__addDocSubmitLock) return;
    if (typeof window !== "undefined") window.__addDocSubmitLock = true;
    if (submittingRef.current) {
      window.__addDocSubmitLock = false;
      return;
    }
    setNameError("");
    if (!name.trim()) {
      setNameError("Document name is required.");
      if (typeof window !== "undefined") window.__addDocSubmitLock = false;
      return;
    }
    if (name.length > MAX_NAME_LENGTH) {
      setNameError("Document name must not exceed 150 characters.");
      if (typeof window !== "undefined") window.__addDocSubmitLock = false;
      return;
    }
    submittingRef.current = true;
    if (isLoading) {
      submittingRef.current = false;
      window.__addDocSubmitLock = false;
      return;
    }
    const scoreVal = Math.min(100, Math.max(0, Math.round(Number(score) || 0)));
    const payload = {
      name: name.trim(),
      score: scoreVal,
      is_compliance: false,
    };
    if (!hideCategoryField) {
      const catId =
        categoryId != null && categoryId !== ""
          ? Number(categoryId)
          : visibleCategories[0]?.id != null
            ? visibleCategories[0].id
            : null;
      if (catId == null && visibleCategories.length > 0) {
        submittingRef.current = false;
        window.__addDocSubmitLock = false;
        return;
      }
      if (catId != null) {
        payload[categoryIdField] = catId;
      }
    }
    onSubmit(payload);
  };

  const handleClose = () => {
    if (!isLoading) {
      setName("");
      setScore(0);
      onClose();
    }
  };

  return (
    <ModalSkeleton
      isOpen={isOpen}
      title="Add New Document"
      closeModal={handleClose}
      modalFooter={null}
      cssClass="w-full max-w-md"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          runSubmit();
        }}
        onKeyDownCapture={(e) => {
          if (e.key === "Enter" && categoryMenuOpenRef.current) {
            skipSubmitRef.current = true;
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            if (skipSubmitRef.current) {
              skipSubmitRef.current = false;
              e.preventDefault();
              return;
            }
            e.preventDefault();
            setTimeout(() => runSubmit(), 0);
          }
        }}
        className="space-y-5"
      >
        {!hideCategoryField && visibleCategories.length === 0 ? (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            {hideCategoryField
              ? "No document groups available. Add a document group before adding documents."
              : "No categories available. Create a category first or run backend migrations for worker compliance categories."}
          </p>
        ) : (
          <>
            <div>
              <InputField
                name="document_name"
                type="text"
                label="Document Name"
                redstar
                placeHolder="e.g. Police Check"
                value={name}
                handleChange={(val) => {
                  setName(val);
                  if (nameError && val.length < MAX_NAME_LENGTH) setNameError("");
                }}
                maxLength={MAX_NAME_LENGTH}
                ErrorMessage={nameError || (name.length >= MAX_NAME_LENGTH ? "Maximum character limit reached." : "")}
              />
              <span
                className="mt-0.5 block"
                style={
                  name.length >= MAX_NAME_LENGTH
                    ? { fontSize: "13px", color: "red", marginLeft: "5px" }
                    : { fontSize: "12px", color: "#6b7280" }
                }
              >
                {name.length}/{MAX_NAME_LENGTH}
              </span>
            </div>
            {!hideCategoryField && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Category <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <SelectOption
                  data={categoryOptions}
                  selectedOption={selectedCategoryOption}
                  handleChange={(option) =>
                    setCategoryId(option?.value != null && option.value !== "" ? String(option.value) : "")
                  }
                  placeHolder="Select Category"
                  isClearable
                  cssClass="w-full"
                  onMenuOpen={() => { categoryMenuOpenRef.current = true; }}
                  onMenuClose={() => { categoryMenuOpenRef.current = false; }}
                />
              </div>
            </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Percentage (%)
              </label>
              <NumericFormat
                value={score}
                onValueChange={(e) => {
                  let val = parseFloat(e.floatValue === undefined ? 0 : e.floatValue);
                  val = Math.round(val);
                  if (val > 100) val = 100;
                  if (val < 0) val = 0;
                  setScore(val);
                }}
                isAllowed={({ floatValue }) =>
                  floatValue === undefined || (floatValue >= 0 && floatValue <= 100)
                }
                allowNegative={false}
                decimalScale={0}
                allowLeadingZeros={false}
                maxLength={4}
                valueIsNumericString={true}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="0"
              />
              <p className="mt-1 text-xs text-gray-500">
                Whole numbers only (e.g. 85). Use 0–100.
              </p>
              <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                <p className="text-xs text-gray-600 leading-relaxed">
                  Mandatory and percentage can be set from the document row after adding.
                </p>
              </div>
            </div>
          </>
        )}
        <div className="flex justify-end gap-x-2 pt-2 border-t border-gray-200 mt-4">
          <CustomButton
            type="button"
            variant="secondary"
            label="Cancel"
            clickHandler={handleClose}
            isDisabled={isLoading}
          />
          <CustomButton
            type="button"
            variant="primary"
            label="Add Document"
            clickHandler={(e) => {
              e.preventDefault();
              runSubmit();
            }}
            showLoading={isLoading}
            isDisabled={
              isLoading ||
              !name.trim() ||
              name.length > MAX_NAME_LENGTH ||
              (!hideCategoryField &&
                visibleCategories.length > 0 &&
                (categoryId === "" || categoryId == null))
            }
          />
        </div>
      </form>
    </ModalSkeleton>
  );
};

export default AddDocumentModal;
